import React, { useState, useEffect } from 'react';
import { formatCurrency } from '../../../utils/currency';
import { 
  collection, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  doc, 
  query, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../../contexts/AuthContext';

const PaymentsPortal = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [activeTab, setActiveTab] = useState('invoices');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(null);

  // Real-time listeners
  useEffect(() => {
    const unsubscribers = [];

    // Invoices listener
    const invoicesQuery = query(
      collection(db, 'invoices'),
      orderBy('issueDate', 'desc')
    );
    const invoicesUnsub = onSnapshot(invoicesQuery, (snapshot) => {
      const invoicesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setInvoices(invoicesData);
    });
    unsubscribers.push(invoicesUnsub);

    // Customers listener
    const customersUnsub = onSnapshot(
      collection(db, 'customers'),
      (snapshot) => {
        const customersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCustomers(customersData);
      }
    );
    unsubscribers.push(customersUnsub);

    // Payments listener
    const paymentsQuery = query(
      collection(db, 'payments'),
      orderBy('paymentDate', 'desc')
    );
    const paymentsUnsub = onSnapshot(paymentsQuery, (snapshot) => {
      const paymentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPayments(paymentsData);
    });
    unsubscribers.push(paymentsUnsub);

    setIsLoading(false);

    return () => unsubscribers.forEach(unsub => unsub());
  }, []);

  // Handle successful payment (reserved for Stripe integration)
  // eslint-disable-next-line no-unused-vars
  const handlePaymentSuccess = async (paymentIntent, invoice) => {
    try {
      // Record payment in Firestore
      await addDoc(collection(db, 'payments'), {
        invoiceId: invoice.id,
        customerId: invoice.customerId,
        amount: paymentIntent.amount / 100, // Convert from cents
        paymentMethod: 'stripe_card',
        paymentDate: new Date().toISOString().split('T')[0],
        stripePaymentIntentId: paymentIntent.id,
        status: 'completed',
        reference: paymentIntent.id,
        notes: `Stripe payment for invoice ${invoice.invoiceNumber}`,
        createdAt: serverTimestamp(),
        createdBy: user?.uid || 'customer'
      });

      // Update invoice status
      const newPaidAmount = (invoice.paidAmount || 0) + (paymentIntent.amount / 100);
      const newBalance = invoice.total - newPaidAmount;
      const newStatus = newBalance <= 0 ? 'paid' : 'partial';

      await updateDoc(doc(db, 'invoices', invoice.id), {
        paidAmount: newPaidAmount,
        balance: newBalance,
        status: newStatus,
        lastPaymentDate: new Date().toISOString().split('T')[0],
        updatedAt: serverTimestamp()
      });

      setPaymentSuccess({
        amount: paymentIntent.amount / 100,
        invoiceNumber: invoice.invoiceNumber,
        paymentId: paymentIntent.id
      });
      setShowPaymentForm(false);
      setSelectedInvoice(null);

      // Auto-hide success message after 5 seconds
      setTimeout(() => {
        setPaymentSuccess(null);
      }, 5000);

    } catch (error) {
      console.error('Error processing payment success:', error);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.fullName : 'Unknown Customer';
  };

  const getInvoiceStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      partial: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.draft;
  };

  const getPayableInvoices = () => {
    return invoices.filter(inv => 
      inv.status !== 'paid' && 
      inv.status !== 'cancelled' && 
      inv.status !== 'draft'
    );
  };

  const getTotalOutstanding = () => {
    return getPayableInvoices().reduce((sum, inv) => sum + (inv.balance || inv.total || 0), 0);
  };

  const getPaidInvoices = () => {
    return invoices.filter(inv => inv.status === 'paid');
  };

  const getRecentPayments = () => {
    return payments.slice(0, 5); // Last 5 payments
  };

  const getPaymentHistory = () => {
    return payments; // All payment history
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-secondary-50 flex items-center justify-center">
        <div className="loading-spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-secondary-900 mb-2">
              Payments Portal
            </h1>
            <p className="text-secondary-600">
              View invoices and make secure payments online
            </p>
          </div>

          {/* Success Message */}
          {paymentSuccess && (
            <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg animate-slide-down">
              <div className="flex items-center">
                <svg className="w-6 h-6 text-green-600 mr-3" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h3 className="text-lg font-semibold text-green-800">Payment Successful!</h3>
                  <p className="text-green-700">
                    Payment of {formatCurrency(paymentSuccess.amount)} for invoice {paymentSuccess.invoiceNumber} has been processed.
                  </p>
                  <p className="text-sm text-green-600 mt-1">
                    Payment ID: {paymentSuccess.paymentId}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-8">
            <div className="card">
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-secondary-600">Outstanding</p>
                    <p className="text-2xl sm:text-3xl font-bold text-secondary-900">{getPayableInvoices().length}</p>
                  </div>
                  <div className="p-2 sm:p-3 rounded-lg bg-red-500 text-white">
                    <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-secondary-600">Amount Due</p>
                    <p className="text-xl sm:text-3xl font-bold text-red-600">
                      {formatCurrency(getTotalOutstanding())}
                    </p>
                  </div>
                  <div className="p-2 sm:p-3 rounded-lg bg-yellow-500 text-white">
                    <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-secondary-600">Paid</p>
                    <p className="text-2xl sm:text-3xl font-bold text-green-600">{getPaidInvoices().length}</p>
                  </div>
                  <div className="p-2 sm:p-3 rounded-lg bg-green-500 text-white">
                    <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-secondary-600">Payments</p>
                    <p className="text-2xl sm:text-3xl font-bold text-primary-600">{getRecentPayments().length}</p>
                  </div>
                  <div className="p-2 sm:p-3 rounded-lg bg-primary-500 text-white">
                    <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mb-8">
            <div className="border-b border-secondary-200">
              <nav className="-mb-px flex space-x-8">
                {[
                  { key: 'invoices', label: 'Outstanding Invoices', count: getPayableInvoices().length },
                  { key: 'paid', label: 'Paid Invoices', count: getPaidInvoices().length },
                  { key: 'payments', label: 'Payment History', count: payments.length }
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-all duration-200 ${
                      activeTab === tab.key
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                    }`}
                  >
                    {tab.label}
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                      activeTab === tab.key
                        ? 'bg-primary-100 text-primary-600'
                        : 'bg-secondary-100 text-secondary-600'
                    }`}>
                      {tab.count}
                    </span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Outstanding Invoices */}
          {activeTab === 'invoices' && (
            <div className="space-y-4">
              {getPayableInvoices().map((invoice) => (
                <div key={invoice.id} className="card-hover">
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start space-y-4 lg:space-y-0">
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-3 space-y-2 sm:space-y-0">
                          <h3 className="text-lg font-semibold text-secondary-900">
                            Invoice #{invoice.invoiceNumber}
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            <span className={`px-2 py-1 rounded text-xs ${getInvoiceStatusColor(invoice.status)}`}>
                              {invoice.status}
                            </span>
                            {new Date(invoice.dueDate) < new Date() && invoice.status !== 'paid' && (
                              <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800">
                                Overdue
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
                          <div>
                            <p className="text-xs sm:text-sm text-secondary-600">Customer</p>
                            <p className="text-sm sm:text-base text-secondary-900 truncate">{getCustomerName(invoice.customerId)}</p>
                          </div>
                          <div>
                            <p className="text-xs sm:text-sm text-secondary-600">Issue Date</p>
                            <p className="text-sm sm:text-base text-secondary-900">{invoice.issueDate}</p>
                          </div>
                          <div>
                            <p className="text-xs sm:text-sm text-secondary-600">Due Date</p>
                            <p className="text-sm sm:text-base text-secondary-900">{invoice.dueDate}</p>
                          </div>
                          <div>
                            <p className="text-xs sm:text-sm text-secondary-600">Amount Due</p>
                            <p className="text-lg sm:text-xl font-semibold text-red-600">
                              {formatCurrency(invoice.balance || invoice.total || 0)}
                            </p>
                            {invoice.paidAmount > 0 && (
                              <p className="text-xs sm:text-sm text-green-600">
                                Paid: {formatCurrency(invoice.paidAmount)}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Invoice Items Preview - Mobile Optimized */}
                        {invoice.items && invoice.items.length > 0 && (
                          <div className="mb-4">
                            <p className="text-xs sm:text-sm text-secondary-600 mb-2">Services:</p>
                            <div className="space-y-1">
                              {invoice.items.slice(0, 2).map((item, index) => (
                                <div key={index} className="flex flex-col sm:flex-row sm:justify-between text-sm space-y-1 sm:space-y-0">
                                  <span className="text-secondary-700 font-medium">{item.description}</span>
                                  <span className="text-secondary-900 text-right">
                                    {item.quantity} × {formatCurrency(item.rate)} = {formatCurrency(item.amount)}
                                  </span>
                                </div>
                              ))}
                              {invoice.items.length > 2 && (
                                <p className="text-xs text-secondary-500">
                                  +{invoice.items.length - 2} more items
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-row lg:flex-col space-x-2 lg:space-x-0 lg:space-y-2 lg:ml-4">
                        <button
                          disabled
                          className="btn-primary btn-sm flex-1 lg:flex-none text-xs sm:text-sm opacity-50 cursor-not-allowed"
                        >
                          <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                          Payments Disabled
                        </button>
                        <button className="btn-secondary btn-sm flex-1 lg:flex-none text-xs sm:text-sm">
                          <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View
                        </button>
                        <button className="btn-secondary btn-sm flex-1 lg:flex-none text-xs sm:text-sm">
                          <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="hidden sm:inline">Download</span>
                          <span className="sm:hidden">PDF</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {getPayableInvoices().length === 0 && (
                <div className="card">
                  <div className="p-12 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                      All Caught Up!
                    </h3>
                    <p className="text-secondary-600">
                      You have no outstanding invoices. All payments are up to date.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Paid Invoices */}
          {activeTab === 'paid' && (
            <div className="space-y-4">
              {getPaidInvoices().map((invoice) => (
                <div key={invoice.id} className="card">
                  <div className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <h3 className="text-lg font-semibold text-secondary-900">
                            Invoice #{invoice.invoiceNumber}
                          </h3>
                          <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-800">
                            Paid
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <p className="text-sm text-secondary-600">Customer</p>
                            <p className="text-secondary-900">{getCustomerName(invoice.customerId)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-secondary-600">Issue Date</p>
                            <p className="text-secondary-900">{invoice.issueDate}</p>
                          </div>
                          <div>
                            <p className="text-sm text-secondary-600">Paid Date</p>
                            <p className="text-secondary-900">{invoice.lastPaymentDate || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-secondary-600">Amount Paid</p>
                            <p className="text-lg font-semibold text-green-600">
                              {formatCurrency(invoice.total || 0)}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col space-y-2 ml-4">
                        <button className="btn-secondary btn-sm">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View
                        </button>
                        <button className="btn-secondary btn-sm">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Download
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Payment History */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              {getPaymentHistory().map((payment) => (
                <div key={payment.id} className="card-hover">
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start space-y-4 lg:space-y-0">
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 mb-3 space-y-2 sm:space-y-0">
                          <h3 className="text-lg font-semibold text-secondary-900">
                            Payment #{payment.id.slice(-8)}
                          </h3>
                          <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-800 w-fit">
                            Completed
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
                          <div>
                            <p className="text-xs sm:text-sm text-secondary-600">Invoice</p>
                            <p className="text-sm sm:text-base text-secondary-900">#{payment.invoiceNumber}</p>
                          </div>
                          <div>
                            <p className="text-xs sm:text-sm text-secondary-600">Payment Date</p>
                            <p className="text-sm sm:text-base text-secondary-900">{payment.date}</p>
                          </div>
                          <div>
                            <p className="text-xs sm:text-sm text-secondary-600">Method</p>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm sm:text-base text-secondary-900 capitalize">{payment.method}</span>
                              {payment.method === 'card' && (
                                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-secondary-600" fill="none" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                              )}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs sm:text-sm text-secondary-600">Amount</p>
                            <p className="text-lg sm:text-xl font-semibold text-green-600">
                              {formatCurrency(payment.amount)}
                            </p>
                          </div>
                        </div>

                        {payment.notes && (
                          <div className="mb-4">
                            <p className="text-xs sm:text-sm text-secondary-600 mb-1">Notes:</p>
                            <p className="text-sm text-secondary-700">{payment.notes}</p>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-row space-x-2 lg:ml-4">
                        <button className="btn-secondary btn-sm flex-1 lg:flex-none text-xs sm:text-sm">
                          <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Details
                        </button>
                        <button className="btn-secondary btn-sm flex-1 lg:flex-none text-xs sm:text-sm">
                          <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V9a2 2 0 00-2-2H9a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-1" />
                          </svg>
                          <span className="hidden sm:inline">Receipt</span>
                          <span className="sm:hidden">PDF</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Payment Form Modal - Mobile Optimized */}
          {showPaymentForm && selectedInvoice && (
            <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
              <div className="card w-full max-w-lg my-8 sm:my-0 animate-scale-in max-h-[90vh] overflow-y-auto">
                <div className="p-4 sm:p-6">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h2 className="text-xl font-bold text-secondary-900">
                        Pay Invoice #{selectedInvoice.invoiceNumber}
                      </h2>
                      <p className="text-secondary-600 text-sm mt-1">
                        Amount Due: <span className="font-semibold">{formatCurrency(selectedInvoice.balance || selectedInvoice.total || 0)}</span>
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setShowPaymentForm(false);
                        setSelectedInvoice(null);
                      }}
                      className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-lg transition-all duration-200"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Payment functionality disabled - Stripe removed */}
                  <div className="text-center py-8">
                    <p className="text-secondary-600">Payment processing is currently disabled.</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
  );
};

export default PaymentsPortal;